/**
 * Thin admin API wrapper. All paths return parsed JSON; non-2xx throw ApiError
 * with the backend's `error` token if present.
 */
import type {
  ChangePasswordRequest,
  LoginRequest,
  LoginResponse,
  ErrorResponse,
  HealthResponse,
  OkResponse,
  ProfileImageResponse,
  RequestResetRequest,
  RequestVerifyRequest,
  ResetPasswordRequest,
  TokenResponse,
} from "@diy-avatar/shared/api";
import type { DeviceSignPubkeyResponse } from "@diy-avatar/shared/e2ee";
import type {
  AddRoleRequest,
  AdminDevicesResponse,
  AdminLogsResponse,
  AuditEventsResponse,
  AdminRoomE2eeStatusResponse,
  AdminMessageRequest,
  AdminMessageResponse,
  AdminRoomsResponse,
  AdminUserResponse,
  AdminUsersResponse,
  AdminUserSessionsResponse,
  Authority,
  AuthoritiesResponse,
  CreateAuthorityRequest,
  CreateSchoolRequest,
  School,
  SchoolsResponse,
  UpdateAuthorityRequest,
  UpdateSchoolRequest,
  CreateDiagLinkRequest,
  CreateDiagLinkResponse,
  ChangeBranchRequest,
  CreateRoomRequest,
  CreateUserRequest,
  DashboardResponse,
  DeviceActionResponse,
  DiagLinksResponse,
  DeviceConfigPatch,
  DeviceConfigResponse,
  DevicesLogListResponse,
  LogEntriesResponse,
  LogSummaryResponse,
  FeatureTogglesPatch,
  FeatureTogglesResponse,
  FlashLogResponse,
  FlashTriggerResponse,
  LedColorMap,
  LedColorsResponse,
  LedTestRequest,
  LedTestResponse,
  RegisterDeviceResponse,
  RenameDeviceRequest,
  SetDeviceDisabledRequest,
  SetDeviceForbidInvisibleRequest,
  SetDeviceForceE2eeQrRequest,
  SetDeviceForceE2eeRequest,
  SetDeviceE2eeRotateHourRequest,
  SpeakerTestRequest,
  SpeakerTestResponse,
  UpdateRoomRequest,
  UpdateUserRequest,
  AdminSettingsResponse,
  PatchSettingsRequest,
  TestEmailRequest,
  TestEmailResponse,
  DashboardLive,
  BackupListResponse,
  CreateBackupResponse,
  LogLevel,
  LogTargetType,
  DebugSessionDTO,
  CreateDebugSessionRequest,
  DebugSessionResponse,
  DebugSessionsResponse,
  DashboardDeviceState,
  MediaCaps,
  MediaCalibration,
  MediaProfile,
} from "@diy-avatar/shared/admin";

/** Append `?label=<encoded>` to a path when a label is provided. */
function withLabel(path: string, label?: string): string {
  if (!label) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}label=${encodeURIComponent(label)}`;
}

/** Merge `{ label }` into a JSON body object when a label is provided. */
function bodyWithLabel<T extends object>(body: T, label?: string): T & { label?: string } {
  return label ? { ...body, label } : body;
}

export const BACKEND_URL =
  (import.meta.env.VITE_BACKEND_URL as string | undefined) ??
  "http://localhost:8085";

export interface MeResponse {
  ok: true;
  email: string;
  display_name: string | null;
  roles: import("@diy-avatar/shared/admin").UserRole[];
  /** When true the user must change their password before continuing. */
  must_change_password?: boolean;
}

export class ApiError extends Error {
  status: number;
  /** Parsed JSON error body, when the backend sent one (e.g. `session_limit`). */
  body: unknown;
  constructor(message: string, status: number, body: unknown = null) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Only declare a JSON content-type when we actually send a body. A bodyless
  // POST/PUT (e.g. /admin/backup, /auth/logout) with `Content-Type:
  // application/json` is rejected by Fastify's JSON parser with 400
  // FST_ERR_CTP_EMPTY_JSON_BODY ("Body cannot be empty ...") before reaching
  // the route handler.
  const headers: Record<string, string> = {
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (init.body != null && !("Content-Type" in headers)) {
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(`${BACKEND_URL}${path}`, {
    credentials: "include",
    ...init,
    headers,
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const msg =
      body &&
      typeof body === "object" &&
      "error" in (body as Record<string, unknown>)
        ? String((body as ErrorResponse).error)
        : `HTTP ${res.status}`;
    throw new ApiError(msg, res.status, body);
  }
  return body as T;
}

// ---- auth ----

/**
 * One active session as reported by the backend in a `session_limit` 409 body.
 * All metadata fields may be absent/unknown server-side and degrade gracefully
 * in the UI (e.g. `ip: null`, `browser: "Unknown"`).
 */
export interface ActiveSession {
  id: string;
  created_at: number | null;
  age_ms: number | null;
  ip: string | null;
  browser: string;
  device: string;
}

/**
 * Parsed JSON error body returned with HTTP 409 when the user hit the
 * max-concurrent-session limit. Lives on {@link ApiError.body}; callers must
 * narrow on `status === 409 && body.error === "session_limit"`.
 */
export interface SessionLimitBody {
  error: "session_limit";
  max_sessions: number;
  active_sessions: ActiveSession[];
}

/**
 * Login. `req.kill_session_ids` (optional, from `LoginRequest`) frees
 * concurrent-session slots on a `session_limit` 409 retry. The backend
 * re-verifies the password before killing the chosen sessions.
 */
export function login(req: LoginRequest): Promise<LoginResponse> {
  return request<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export function logout(): Promise<{ ok: true }> {
  return request<{ ok: true }>("/auth/logout", { method: "POST" });
}

export function me(): Promise<MeResponse> {
  return request<MeResponse>("/auth/me", { method: "GET" });
}

/** Self-service password change (session-authed). Clears must_change_password. */
export function changePassword(
  req: ChangePasswordRequest,
): Promise<OkResponse> {
  return request<OkResponse>("/me/change-password", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

// ---- account lifecycle (email verification + password reset) ----

/** (Re)send a verification email. Always resolves 200 (no enumeration). */
export function requestVerify(req: RequestVerifyRequest = {}): Promise<OkResponse> {
  return request<OkResponse>("/auth/request-verify", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

/** Consume a verification token (from the /verify/:token page). */
export function verifyEmail(token: string): Promise<OkResponse> {
  return request<OkResponse>(`/auth/verify/${encodeURIComponent(token)}`, {
    method: "GET",
  });
}

/** Request a password-reset link. Always resolves 200 (no enumeration). */
export function requestReset(req: RequestResetRequest): Promise<OkResponse> {
  return request<OkResponse>("/auth/request-reset", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

/** Set a new password from a reset token (from the /reset/:token page). */
export function resetPassword(
  token: string,
  req: ResetPasswordRequest,
): Promise<OkResponse> {
  return request<OkResponse>(`/auth/reset/${encodeURIComponent(token)}`, {
    method: "POST",
    body: JSON.stringify(req),
  });
}

// ---- dashboard ----

export function fetchDashboard(): Promise<DashboardResponse> {
  return request<DashboardResponse>("/admin/dashboard", { method: "GET" });
}

/** Aggregated live-monitoring snapshot for the admin dashboard. */
export function fetchDashboardLive(): Promise<DashboardLive> {
  return request<DashboardLive>("/admin/dashboard/live", { method: "GET" });
}

// ---- profile image (read another participant's avatar) ----

/**
 * Read a student's stored avatar by numeric id or email. Returns an absolute
 * image URL or null when unset. Backed by `GET /students/:idOrEmail/profile-image`.
 */
export async function fetchStudentProfileImage(
  idOrEmail: string,
): Promise<string | null> {
  const res = await request<ProfileImageResponse>(
    `/students/${encodeURIComponent(idOrEmail)}/profile-image`,
    { method: "GET" },
  );
  // Backend now returns a root-relative path (e.g. "/profile-images/<hash>.jpg");
  // prepend BACKEND_URL so `<img src>` hits the backend origin, not nginx.
  if (typeof res.image === "string" && res.image.startsWith("/")) {
    return `${BACKEND_URL}${res.image}`;
  }
  return res.image;
}

// ---- server health ----

export function fetchHealth(): Promise<HealthResponse> {
  return request<HealthResponse>("/healthz", { method: "GET" });
}

// ---- logs ----

export function fetchLogs(opts: {
  since?: number;
  limit?: number;
  /** Lower time bound (ISO date or epoch ms) — server-side filter. */
  from?: string | number;
  /** Upper time bound (ISO date or epoch ms) — server-side filter. */
  to?: string | number;
  /** Match by username/email or user id — server-side filter. */
  user?: string;
  /** Free-text/event query — server-side filter. */
  q?: string;
  /** Match by device label substring — server-side filter. */
  device?: string;
  /** Match by school id — server-side filter. */
  school?: string;
  /** Comma-separated severity list (e.g. "warn,error") — server-side filter. */
  level?: string;
  /** Exact audit event name — server-side filter (distinct from free-text q). */
  event?: string;
  /** Match by room id — server-side filter. */
  room?: string | number;
} = {}): Promise<AdminLogsResponse> {
  const params = new URLSearchParams();
  if (opts.since !== undefined) params.set("since", String(opts.since));
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.from !== undefined && opts.from !== "") params.set("from", String(opts.from));
  if (opts.to !== undefined && opts.to !== "") params.set("to", String(opts.to));
  if (opts.user !== undefined && opts.user !== "") params.set("user", opts.user);
  if (opts.q !== undefined && opts.q !== "") params.set("q", opts.q);
  if (opts.device !== undefined && opts.device !== "") params.set("device", opts.device);
  if (opts.school !== undefined && opts.school !== "") params.set("school", opts.school);
  if (opts.level !== undefined && opts.level !== "") params.set("level", opts.level);
  if (opts.event !== undefined && opts.event !== "") params.set("event", opts.event);
  if (opts.room !== undefined && opts.room !== "") params.set("room", String(opts.room));
  const qs = params.toString();
  return request<AdminLogsResponse>(`/admin/logs${qs ? `?${qs}` : ""}`, {
    method: "GET",
  });
}

/** Distinct audit event names (sorted) for the event-filter dropdown. */
export function listAuditEvents(): Promise<AuditEventsResponse> {
  return request<AuditEventsResponse>("/admin/logs/events", { method: "GET" });
}

// ---- device logs ----

export function listDevicesLogs(): Promise<DevicesLogListResponse> {
  return request<DevicesLogListResponse>("/admin/device-logs", { method: "GET" });
}

// ---- app logs (logs.db viewer: Diagnostic-log tab) ----

/**
 * Query the unified log store (logs.db). Empty/undefined params are omitted.
 * `since`/`until` are epoch-ms or ISO; `level` is a comma list ("all" = no
 * filter); `before_id` is a cursor; `q` is full-text (FTS). The response's
 * `next_cursor` is a ts-based cursor — pass it back as `until` to page older.
 */
export function listAppLogs(opts: {
  target_type?: LogTargetType;
  target_id?: string;
  session_id?: string;
  level?: string;
  since?: number | string;
  until?: number | string;
  limit?: number;
  before_id?: string;
  q?: string;
} = {}): Promise<LogEntriesResponse> {
  const params = new URLSearchParams();
  if (opts.target_type !== undefined) params.set("target_type", opts.target_type);
  if (opts.target_id !== undefined && opts.target_id !== "")
    params.set("target_id", opts.target_id);
  if (opts.session_id !== undefined && opts.session_id !== "")
    params.set("session_id", opts.session_id);
  if (opts.level !== undefined && opts.level !== "")
    params.set("level", opts.level);
  if (opts.since !== undefined && opts.since !== "")
    params.set("since", String(opts.since));
  if (opts.until !== undefined && opts.until !== "")
    params.set("until", String(opts.until));
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.before_id !== undefined && opts.before_id !== "")
    params.set("before_id", opts.before_id);
  if (opts.q !== undefined && opts.q !== "") params.set("q", opts.q);
  const qs = params.toString();
  return request<LogEntriesResponse>(
    `/admin/log-entries${qs ? `?${qs}` : ""}`,
    { method: "GET" },
  );
}

/** Error/warn summary + top messages over a window (Diagnostic-log tab). */
export function appLogSummary(opts: {
  target_type?: LogTargetType;
  target_id?: string;
  since?: number | string;
  until?: number | string;
} = {}): Promise<LogSummaryResponse> {
  const params = new URLSearchParams();
  if (opts.target_type !== undefined) params.set("target_type", opts.target_type);
  if (opts.target_id !== undefined && opts.target_id !== "")
    params.set("target_id", opts.target_id);
  if (opts.since !== undefined && opts.since !== "")
    params.set("since", String(opts.since));
  if (opts.until !== undefined && opts.until !== "")
    params.set("until", String(opts.until));
  const qs = params.toString();
  return request<LogSummaryResponse>(
    `/admin/log-entries/summary${qs ? `?${qs}` : ""}`,
    { method: "GET" },
  );
}

// ---- diagnostic links ----

export function createDiagLink(
  req: CreateDiagLinkRequest,
): Promise<CreateDiagLinkResponse> {
  return request<CreateDiagLinkResponse>("/admin/diag-links", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export function listDiagLinks(): Promise<DiagLinksResponse> {
  return request<DiagLinksResponse>("/admin/diag-links", { method: "GET" });
}

export function regenerateDiagLink(
  token: string,
  ttlHours?: number,
): Promise<CreateDiagLinkResponse> {
  return request<CreateDiagLinkResponse>(
    `/admin/diag-links/${encodeURIComponent(token)}/regenerate`,
    {
      method: "POST",
      body: JSON.stringify({ ttl_hours: ttlHours }),
    },
  );
}

export function revokeDiagLink(token: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/admin/diag-links/${encodeURIComponent(token)}`,
    { method: "DELETE" },
  );
}

// ---- per-target log levels ----

/** Set a device's effective log verbosity. */
export async function setDeviceLogLevel(
  label: string,
  level: LogLevel,
): Promise<void> {
  await request<OkResponse>(
    `/admin/devices/${encodeURIComponent(label)}/loglevel`,
    { method: "PATCH", body: JSON.stringify({ level }) },
  );
}

/** Set a user's effective log verbosity. */
export async function setUserLogLevel(
  id: number,
  level: LogLevel,
): Promise<void> {
  await request<OkResponse>(`/admin/users/${id}/loglevel`, {
    method: "POST",
    body: JSON.stringify({ level }),
  });
}

// ---- debug sessions (time/budget-scoped log boosts) ----

/** Start a debug session that raises a target's log level for a period. */
export async function createDebugSession(
  req: CreateDebugSessionRequest,
): Promise<DebugSessionDTO> {
  const res = await request<DebugSessionResponse>("/admin/debug-sessions", {
    method: "POST",
    body: JSON.stringify(req),
  });
  return res.session;
}

/** List debug sessions, optionally filtered by active state / target. */
export async function listDebugSessions(opts: {
  active?: boolean;
  target_type?: LogTargetType;
  target_id?: string;
} = {}): Promise<DebugSessionDTO[]> {
  const params = new URLSearchParams();
  if (opts.active !== undefined) params.set("active", String(opts.active));
  if (opts.target_type !== undefined) params.set("target_type", opts.target_type);
  if (opts.target_id !== undefined && opts.target_id !== "")
    params.set("target_id", opts.target_id);
  const qs = params.toString();
  const res = await request<DebugSessionsResponse>(
    `/admin/debug-sessions${qs ? `?${qs}` : ""}`,
    { method: "GET" },
  );
  return res.sessions;
}

/** Stop (deactivate) a debug session by id. */
export async function stopDebugSession(id: number): Promise<void> {
  await request<{ ok: true }>(`/admin/debug-sessions/${id}`, {
    method: "DELETE",
  });
}

// ---- device registry + actions ----

export function listDevices(): Promise<AdminDevicesResponse> {
  return request<AdminDevicesResponse>("/admin/devices", { method: "GET" });
}

export function registerDevice(label: string): Promise<RegisterDeviceResponse> {
  return request<RegisterDeviceResponse>("/admin/devices", {
    method: "POST",
    body: JSON.stringify({ label }),
  });
}

export function revokeDevice(label: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/admin/devices/${encodeURIComponent(label)}`,
    { method: "DELETE" },
  );
}

export function rebootDevice(label: string): Promise<DeviceActionResponse> {
  return request<DeviceActionResponse>(
    `/admin/devices/${encodeURIComponent(label)}/reboot`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function updateDevice(label: string): Promise<DeviceActionResponse> {
  return request<DeviceActionResponse>(
    `/admin/devices/${encodeURIComponent(label)}/update`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export interface AdminDeviceDiagnosticsResponse {
  ok: true;
  device: DashboardDeviceState & {
    media_caps_ts: number | null;
    media_caps_schema_version: number | null;
    media_caps: MediaCaps | null;
    media_calibration_ts: number | null;
    media_calibration_schema_version: number | null;
    media_calibration: MediaCalibration | null;
    media_profile: MediaProfile | null;
  };
  generated_at: number;
}

export function getDeviceDiagnostics(
  label: string,
): Promise<AdminDeviceDiagnosticsResponse> {
  return request<AdminDeviceDiagnosticsResponse>(
    `/admin/devices/${encodeURIComponent(label)}/diagnostics`,
  );
}

export function mediaProbeRecheck(label: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/admin/devices/${encodeURIComponent(label)}/media-probe-recheck`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function changeDeviceBranch(
  label: string,
  channel: ChangeBranchRequest["channel"],
): Promise<DeviceActionResponse> {
  return request<DeviceActionResponse>(
    `/admin/devices/${encodeURIComponent(label)}/branch`,
    { method: "POST", body: JSON.stringify({ channel }) },
  );
}

export function changeDevicePin(
  label: string,
  pin: string,
): Promise<DeviceActionResponse> {
  return request<DeviceActionResponse>(
    `/admin/devices/${encodeURIComponent(label)}/pin`,
    { method: "POST", body: JSON.stringify({ pin }) },
  );
}

export function renameDevice(
  label: string,
  newLabel: string,
): Promise<DeviceActionResponse> {
  const body: RenameDeviceRequest = { label: newLabel };
  return request<DeviceActionResponse>(
    `/admin/devices/${encodeURIComponent(label)}/rename`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function setDeviceDisabled(
  label: string,
  disabled: boolean,
): Promise<DeviceActionResponse> {
  const body: SetDeviceDisabledRequest = { disabled };
  return request<DeviceActionResponse>(
    `/admin/devices/${encodeURIComponent(label)}/disabled`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** Set a device's `forbid_invisible` flag (forbid students' invisible mode). */
export function setDeviceForbidInvisible(
  label: string,
  forbid_invisible: boolean,
): Promise<DeviceActionResponse> {
  const body: SetDeviceForbidInvisibleRequest = { forbid_invisible };
  return request<DeviceActionResponse>(
    `/admin/devices/${encodeURIComponent(label)}/forbid-invisible`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** Set a device's `force_e2ee_qr` flag (force in-person QR key exchange). */
export function setDeviceForceE2eeQr(
  label: string,
  force_e2ee_qr: boolean,
): Promise<DeviceActionResponse> {
  const body: SetDeviceForceE2eeQrRequest = { force_e2ee_qr };
  return request<DeviceActionResponse>(
    `/admin/devices/${encodeURIComponent(label)}/force-e2ee-qr`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** Set a device's `force_e2ee` flag (require E2EE; auto-seal stays on). */
export function setDeviceForceE2ee(
  label: string,
  force_e2ee: boolean,
): Promise<DeviceActionResponse> {
  const body: SetDeviceForceE2eeRequest = { force_e2ee };
  return request<DeviceActionResponse>(
    `/admin/devices/${encodeURIComponent(label)}/force-e2ee`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/**
 * Set a device's `e2ee_rotate_hour` (0-23 local), or null to clear/inherit the
 * daily E2EE key-rotation hour from the parent (room → school → authority).
 */
export function setDeviceE2eeRotateHour(
  label: string,
  e2ee_rotate_hour: number | null,
): Promise<DeviceActionResponse> {
  const body: SetDeviceE2eeRotateHourRequest = { e2ee_rotate_hour };
  return request<DeviceActionResponse>(
    `/admin/devices/${encodeURIComponent(label)}/e2ee-rotate-hour`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/**
 * Read a device's Ed25519 sign public key (base64url), or null when the device
 * has not registered one. The e2ee routes are mounted at root (no `/admin`
 * prefix), so this hits `GET /devices/:label/e2ee/sign-pubkey`.
 */
export function getDeviceSignPubkey(
  label: string,
): Promise<DeviceSignPubkeyResponse> {
  return request<DeviceSignPubkeyResponse>(
    `/devices/${encodeURIComponent(label)}/e2ee/sign-pubkey`,
    { method: "GET" },
  );
}

/** Assign (or clear, with `roomId: null`) the room a device lives in. */
export function setDeviceRoom(
  label: string,
  roomId: number | null,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/admin/devices/${encodeURIComponent(label)}/room`,
    { method: "PATCH", body: JSON.stringify({ room_id: roomId }) },
  );
}

// ---- users ----

export function listUsers(): Promise<AdminUsersResponse> {
  return request<AdminUsersResponse>("/admin/users", { method: "GET" });
}
export function createUser(req: CreateUserRequest): Promise<{
  ok: true;
  user: AdminUsersResponse["users"][number];
}> {
  return request("/admin/users", {
    method: "POST",
    body: JSON.stringify(req),
  });
}
export function updateUser(
  id: number,
  patch: UpdateUserRequest,
): Promise<{ ok: true; user: AdminUsersResponse["users"][number] }> {
  return request(`/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}
export function deleteUser(id: number): Promise<{ ok: true }> {
  return request(`/admin/users/${id}`, { method: "DELETE" });
}
/** Grant a role (with optional authority/school scope) to a user. */
export function addUserRole(
  id: number,
  req: AddRoleRequest,
): Promise<AdminUserResponse> {
  return request<AdminUserResponse>(`/admin/users/${id}/roles`, {
    method: "POST",
    body: JSON.stringify(req),
  });
}
/** Remove a single role assignment (by role-assignment id) from a user. */
export function removeUserRole(
  id: number,
  roleId: number,
): Promise<AdminUserResponse> {
  return request<AdminUserResponse>(`/admin/users/${id}/roles/${roleId}`, {
    method: "DELETE",
  });
}
/** Resend the verification email to an unverified user. */
export function resendUserVerification(id: number): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/admin/users/${id}/resend-verification`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}
/** Send a password-reset link to a user. */
export function sendUserReset(id: number): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/admin/users/${id}/send-reset`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}
/** Fetch a single user by id (scope-filtered like the user list). */
export function getUser(id: number): Promise<AdminUserResponse> {
  return request<AdminUserResponse>(`/admin/users/${id}`, { method: "GET" });
}
/** List a user's active login sessions (newest first) + last-login time. */
export function getUserSessions(
  id: number,
): Promise<AdminUserSessionsResponse> {
  return request<AdminUserSessionsResponse>(`/admin/users/${id}/sessions`, {
    method: "GET",
  });
}
/** Set or clear (image: null) another user's profile avatar. */
export function setUserProfileImage(
  id: number,
  body: { image: string | null; imageFull?: string | null },
): Promise<ProfileImageResponse> {
  return request<ProfileImageResponse>(`/admin/users/${id}/profile-image`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ---- authorities ----

export function listAuthorities(): Promise<AuthoritiesResponse> {
  return request<AuthoritiesResponse>("/admin/authorities", { method: "GET" });
}
export function createAuthority(
  req: CreateAuthorityRequest,
): Promise<{ ok: true; authority: Authority }> {
  return request("/admin/authorities", {
    method: "POST",
    body: JSON.stringify(req),
  });
}
export function updateAuthority(
  id: number,
  patch: UpdateAuthorityRequest,
): Promise<{ ok: true; authority: Authority }> {
  return request(`/admin/authorities/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}
export function deleteAuthority(id: number): Promise<{ ok: true }> {
  return request(`/admin/authorities/${id}`, { method: "DELETE" });
}

// ---- schools ----

export function listSchools(): Promise<SchoolsResponse> {
  return request<SchoolsResponse>("/admin/schools", { method: "GET" });
}
export function createSchool(
  req: CreateSchoolRequest,
): Promise<{ ok: true; school: School }> {
  return request("/admin/schools", {
    method: "POST",
    body: JSON.stringify(req),
  });
}
export function updateSchool(
  id: number,
  patch: UpdateSchoolRequest,
): Promise<{ ok: true; school: School }> {
  return request(`/admin/schools/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}
export function deleteSchool(id: number): Promise<{ ok: true }> {
  return request(`/admin/schools/${id}`, { method: "DELETE" });
}

// ---- rooms ----

export function listRooms(): Promise<AdminRoomsResponse> {
  return request<AdminRoomsResponse>("/admin/rooms", { method: "GET" });
}
export function createRoom(
  req: CreateRoomRequest,
): Promise<{ ok: true; room?: AdminRoomsResponse["rooms"][number] }> {
  return request("/admin/rooms", {
    method: "POST",
    body: JSON.stringify(req),
  });
}
export function updateRoom(
  id: number,
  patch: UpdateRoomRequest,
): Promise<{ ok: true; room?: AdminRoomsResponse["rooms"][number] }> {
  return request(`/admin/rooms/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}
export function deleteRoom(id: number): Promise<{ ok: true }> {
  return request(`/admin/rooms/${id}`, { method: "DELETE" });
}
/**
 * Live (in-memory) E2EE status of a room: the negotiated whole-room
 * `encryption_mode` + the most recent auto-seal failure, if any. Returns 404
 * when E2EE is globally disabled (gated server-side). Admin-only.
 */
export function fetchRoomE2eeStatus(
  id: number,
): Promise<AdminRoomE2eeStatusResponse> {
  return request<AdminRoomE2eeStatusResponse>(
    `/admin/rooms/${id}/e2ee-status`,
    { method: "GET" },
  );
}
export function setRoomTeachers(
  id: number,
  emails: string[],
): Promise<{ ok: true; room?: AdminRoomsResponse["rooms"][number] }> {
  return request(`/admin/rooms/${id}/teachers`, {
    method: "PUT",
    body: JSON.stringify({ emails }),
  });
}
export function setRoomStudents(
  id: number,
  emails: string[],
): Promise<{ ok: true; room?: AdminRoomsResponse["rooms"][number] }> {
  return request(`/admin/rooms/${id}/students`, {
    method: "PUT",
    body: JSON.stringify({ emails }),
  });
}

// ---- message ----

export function sendMessage(
  req: AdminMessageRequest,
  label?: string,
): Promise<AdminMessageResponse> {
  return request<AdminMessageResponse>("/admin/message", {
    method: "POST",
    body: JSON.stringify(bodyWithLabel(req, label)),
  });
}

// ---- device config ----

export function fetchDeviceConfig(label?: string): Promise<DeviceConfigResponse> {
  return request<DeviceConfigResponse>(withLabel("/admin/device-config", label), {
    method: "GET",
  });
}
export function patchDeviceConfig(
  patch: DeviceConfigPatch,
  label?: string,
): Promise<DeviceConfigResponse> {
  return request<DeviceConfigResponse>("/admin/device-config", {
    method: "PATCH",
    body: JSON.stringify(bodyWithLabel(patch, label)),
  });
}

// ---- feature toggles ----

export function fetchToggles(label?: string): Promise<FeatureTogglesResponse> {
  return request<FeatureTogglesResponse>(
    withLabel("/admin/feature-toggles", label),
    { method: "GET" },
  );
}
export function patchToggles(
  patch: FeatureTogglesPatch,
  label?: string,
): Promise<FeatureTogglesResponse> {
  return request<FeatureTogglesResponse>("/admin/feature-toggles", {
    method: "PATCH",
    body: JSON.stringify(bodyWithLabel(patch, label)),
  });
}

// ---- led colors ----

export function fetchLedColors(label?: string): Promise<LedColorsResponse> {
  return request<LedColorsResponse>(withLabel("/admin/led-colors", label), {
    method: "GET",
  });
}
export function patchLedColors(
  patch: LedColorMap,
  label?: string,
): Promise<LedColorsResponse> {
  return request<LedColorsResponse>("/admin/led-colors", {
    method: "PATCH",
    body: JSON.stringify(bodyWithLabel(patch, label)),
  });
}

// ---- led testmode ----

export function sendLedTest(
  req: LedTestRequest,
  label?: string,
): Promise<LedTestResponse> {
  return request<LedTestResponse>("/admin/led-test", {
    method: "POST",
    body: JSON.stringify(bodyWithLabel(req, label)),
  });
}

// ---- speaker test ----

export function sendSpeakerTest(
  req: SpeakerTestRequest = {},
  label?: string,
): Promise<SpeakerTestResponse> {
  return request<SpeakerTestResponse>("/admin/speaker-test", {
    method: "POST",
    body: JSON.stringify(bodyWithLabel(req, label)),
  });
}

// ---- camera preview (per-device) ----

export interface CameraPreviewAck {
  ok: true;
  id: string;
}

export interface CameraPreviewFrame {
  ok: true;
  path: string;
  /** Present on a successful capture. Absent when `error` is set. */
  jpeg_b64?: string;
  /** Present when the device could not capture a frame (e.g. device busy). */
  error?: string;
  ts: number;
}

/**
 * Ask the device to capture a still preview from `path` for `ttlS` seconds.
 * Per-device: the label is merged into the body the same way sendSpeakerTest does.
 */
export function requestCameraPreview(
  path: string,
  ttlS: number,
  label?: string,
): Promise<CameraPreviewAck> {
  return request<CameraPreviewAck>("/admin/camera-preview", {
    method: "POST",
    body: JSON.stringify(bodyWithLabel({ path, ttl_s: ttlS }, label)),
  });
}

/**
 * Poll for the latest captured preview frame for `path`. Returns the parsed
 * body, or `null` on HTTP 204 (no frame captured yet).
 */
export async function fetchCameraPreview(
  path: string,
  label?: string,
): Promise<CameraPreviewFrame | null> {
  const qs = withLabel(
    `/admin/camera-preview?path=${encodeURIComponent(path)}`,
    label,
  );
  const res = await fetch(`${BACKEND_URL}${qs}`, {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (res.status === 204) return null;
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const msg =
      body &&
      typeof body === "object" &&
      "error" in (body as Record<string, unknown>)
        ? String((body as ErrorResponse).error)
        : `HTTP ${res.status}`;
    throw new ApiError(msg, res.status, body);
  }
  return body as CameraPreviewFrame;
}

// ---- flash firmware (Topic D) ----

/**
 * Flash the prebuilt REPO firmware for `board` to the device's ESP. Returns the
 * new `flash_id` to poll `fetchFlashLog` with. The flash is FORCED on the agent
 * side (esptool always runs).
 */
export function flashFirmware(
  label: string,
  opts: { source: "repo"; board?: "esp8266" | "esp32" },
): Promise<FlashTriggerResponse> {
  return request<FlashTriggerResponse>(
    `/admin/devices/${encodeURIComponent(label)}/flash`,
    {
      method: "POST",
      body: JSON.stringify({ source: opts.source, board: opts.board }),
    },
  );
}

/**
 * Upload a CUSTOM firmware `.bin` (base64) and flash it instead of the repo
 * image. ESP32 custom images must be full/merged (flashed at 0x0).
 */
export function flashFirmwareUpload(
  label: string,
  firmwareB64: string,
  board?: "esp8266" | "esp32",
  filename?: string,
): Promise<FlashTriggerResponse> {
  return request<FlashTriggerResponse>(
    `/admin/devices/${encodeURIComponent(label)}/flash-upload`,
    {
      method: "POST",
      body: JSON.stringify({ firmware_b64: firmwareB64, board, filename }),
    },
  );
}

/**
 * Poll incremental esptool output for a flash. `since` is the cursor returned as
 * `next` by the previous call (start at 0). Keeps polling until `status` is
 * "ok"/"error".
 */
export function fetchFlashLog(
  label: string,
  flashId: string,
  since: number,
): Promise<FlashLogResponse> {
  return request<FlashLogResponse>(
    `/admin/devices/${encodeURIComponent(label)}/flash-log?flash_id=${encodeURIComponent(
      flashId,
    )}&since=${since}`,
    { method: "GET" },
  );
}

// ---- livekit token (admin subscribe) ----

/** Mint an admin LiveKit token (canSubscribe) for `room`. */
export function fetchAdminLivekitToken(room: string): Promise<TokenResponse> {
  return request<TokenResponse>("/livekit/token", {
    method: "POST",
    body: JSON.stringify({ room }),
  });
}

/** List admin rooms (used to find the room bound to a device label). */
export function fetchAdminRooms(): Promise<AdminRoomsResponse> {
  return request<AdminRoomsResponse>("/admin/rooms", { method: "GET" });
}

// ---- releases (Topic R) ----

export type ReleaseChannel = "LIVE" | "BETA";

export interface ReleaseTag {
  version: string;
  commit: string;
  date: string;
  message: string;
}

export interface ReleasePointer {
  target_tag: string;
  commit: string;
  promoted_at: string;
  promoted_by: string;
}

export interface ReleasesResponse {
  tags: ReleaseTag[];
  pointers: {
    LIVE: ReleasePointer | null;
    BETA: ReleasePointer | null;
  };
  /** ISO timestamp of the last successful git-mirror fetch, or null. */
  last_synced: string | null;
  /** Number of release tags currently known to the mirror. */
  tag_count: number;
  /** Message of the last FAILED mirror fetch, null when the last one worked. */
  last_error: string | null;
}

export interface PromoteResponse {
  ok: true;
  channel: ReleaseChannel;
  target_tag: string;
}

/** Downgrade conflict payload returned with HTTP 409. */
export interface DowngradeConflict {
  error: "downgrade";
  current: string;
}

/** List git tags + current LIVE/BETA channel pointers. */
export function getReleases(): Promise<ReleasesResponse> {
  return request<ReleasesResponse>("/admin/releases", { method: "GET" });
}

/** Force a git-mirror fetch, then return the refreshed releases view. */
export function refreshReleases(): Promise<ReleasesResponse> {
  return request<ReleasesResponse>("/admin/releases/refresh", { method: "POST" });
}

/**
 * Promote `target_tag` onto a channel. On a downgrade the backend returns HTTP
 * 409 with `{ error: "downgrade", current }`; pass `force: true` to override.
 */
export function promoteRelease(
  channel: ReleaseChannel,
  target_tag: string,
  force = false,
): Promise<PromoteResponse> {
  return request<PromoteResponse>("/admin/releases/promote", {
    method: "POST",
    body: JSON.stringify({ channel, target_tag, force }),
  });
}

// ---- device update fan-out ----

export interface UpdateAllResponse {
  ok: true;
  queued: number;
}

/** Queue an update for every device on `channel`. */
export function updateAllDevices(
  channel: ReleaseChannel,
): Promise<UpdateAllResponse> {
  return request<UpdateAllResponse>("/admin/devices/update-all", {
    method: "POST",
    body: JSON.stringify({ channel }),
  });
}

// ---- infra (server-side deploy + health) ----

export interface InfraGitStatus {
  /** Authoritative running backend version (build-injected). */
  running_version: string | null;
  /** Running backend git sha. */
  running_commit: string | null;
  /** Host infra repo tag. */
  deployed_tag: string | null;
  deployed_commit: string | null;
  deployed_at: string | null;
  channel: string | null;
  /** null = UNKNOWN (do NOT treat as dirty). */
  clean: boolean | null;
  releases_behind: number | null;
  commits_behind: number | null;
  target_tag: string | null;
  /** false when host repo git reads failed. */
  repo_ok: boolean;
  /** Per-field failure reasons; keys: deployed_tag, deployed_commit, deployed_at, channel, clean, drift. */
  errors?: Record<string, string>;
  /**
   * Can the backend drive docker itself? FALSE on a stock install (D47.2): the
   * root-equivalent host socket is opt-in. Informational only since D48 — the
   * dashboard no longer triggers updates. Optional so an older backend (no such
   * field) does not break the UI.
   */
  docker_available?: boolean;
}

/** Current deploy state of the server checkout vs its channel target. */
export function getInfraGitStatus(): Promise<InfraGitStatus> {
  return request<InfraGitStatus>("/admin/infra/git-status", { method: "GET" });
}

export type CheckStatus = "OK" | "WARN" | "FAIL";

export interface CheckAliveService {
  name: string;
  status: CheckStatus;
  detail: string;
}

export interface CheckAliveResult {
  /** Epoch milliseconds the check ran, or null when it has never run. */
  ran_at: number | null;
  overall: CheckStatus;
  services: CheckAliveService[];
  raw: string;
}

/** Run check-alive fresh and return its result. */
export function runCheckAlive(): Promise<CheckAliveResult> {
  return request<CheckAliveResult>("/admin/infra/check-alive", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/** Read the cached check-alive result; null when never run. */
export function getCheckAlive(): Promise<CheckAliveResult | null> {
  return request<CheckAliveResult | null>("/admin/infra/check-alive", {
    method: "GET",
  });
}

// NOTE (D48): the infra UPDATE client (startInfraUpdate,
// startInfraSwitchChannel, getInfraUpdate*, host-updater trigger/status/log)
// was REMOVED together with the admin-triggered updater. Updates run over SSH
// (`bash infra/update.sh`). Read-only infra calls above stay.

// ---- website settings (SMTP + recipients + Altcha) ----

export function getSettings(): Promise<AdminSettingsResponse> {
  return request<AdminSettingsResponse>("/admin/settings", { method: "GET" });
}

export function patchSettings(
  patch: PatchSettingsRequest,
): Promise<AdminSettingsResponse> {
  return request<AdminSettingsResponse>("/admin/settings", {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function sendTestEmail(
  req: TestEmailRequest = {},
): Promise<TestEmailResponse> {
  return request<TestEmailResponse>("/admin/settings/test-email", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

// ---- backups (SQLite snapshots) ----

export function listBackups(): Promise<BackupListResponse> {
  return request<BackupListResponse>("/admin/backup", { method: "GET" });
}

export function createBackup(): Promise<CreateBackupResponse> {
  return request<CreateBackupResponse>("/admin/backup", { method: "POST" });
}

export function deleteBackup(filename: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/admin/backup/${encodeURIComponent(filename)}`,
    { method: "DELETE" },
  );
}

/** Direct URL for credentialed blob download of a backup zip. */
export function backupDownloadUrl(filename: string): string {
  return `${BACKEND_URL}/admin/backup/${encodeURIComponent(filename)}`;
}

export const __test = { BACKEND_URL };
